import React, {useRef, useState} from 'react';
import {
  PanResponder,
  Pressable,
  StyleSheet,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  type ViewStyle,
} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {DayKind, MonthCell} from '@/entities/calendar/month';
import {
  TAP_SLOP,
  datesAlong,
  hitTest,
  paint,
  type PaintMode,
  type Point,
  type Rect,
} from '../lib/drag';
import {formatDayMinutes} from '@/shared/lib/format';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha, type Palette} from '@/shared/ui/theme';

/**
 * Месяц графика сеткой 7×N, неделя с понедельника.
 *
 * Клетка говорит три вещи, по важности: число, что это за день (цвет и короткая
 * подпись) и, если рабочий, часы. Праздник, выходной и перенос — по цвету тех же
 * значков, что в календаре праздников; запись, пришедшая от компании или
 * вышестоящего отдела, помечена точкой — её нельзя удалить отсюда, только перекрыть.
 *
 * Выделение нескольких дней: нажатие отмечает день, Shift+клик — диапазон от
 * прошлого отмеченного, нажатие на заголовок столбца — все такие дни месяца, а
 * протягивание (зажал и повёл мышью или пальцем) — каждый задетый день. Протягивание,
 * начатое с выделенного дня, снимает выделение. Пока ведёшь, сетка показывает, что
 * получится; решение уходит наверх одним вызовом на отпускании.
 * Выделенный день — подложка цвета акцента и галочка; «сегодня» — только рамка, чтобы
 * их нельзя было спутать.
 */
const styles = StyleSheet.create({
  // Shift+клик в браузере выделял бы текст клеток вместо диапазона дней.
  root: {gap: spacing(1), userSelect: 'none'} as ViewStyle,
  body: {gap: spacing(1)},
  row: {flexDirection: 'row', gap: spacing(1)},
  head: {flex: 1, alignItems: 'center', paddingVertical: spacing(1), borderRadius: radii.md},
  cell: {
    flex: 1,
    minWidth: 0,
    borderRadius: radii.md,
    borderWidth: 1,
    padding: spacing(1),
    gap: 2,
  },
  top: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  dot: {width: 6, height: 6, borderRadius: 3},
});

type Skin = {bg: string; border: string; ink: string};

export function skinOf(kind: DayKind, p: Palette): Skin {
  switch (kind) {
    case 'holiday':
      return {bg: p.infoSoft, border: withAlpha(p.info, 0.45), ink: p.infoInk};
    case 'day_off':
      return {bg: p.muted, border: p.borderStrong, ink: p.mutedForeground};
    case 'workday':
      return {bg: withAlpha(p.signal, 0.12), border: withAlpha(p.signal, 0.5), ink: p.signalInk};
    case 'off':
      return {bg: p.muted, border: p.border, ink: p.mutedForeground};
    case 'work':
    case 'plain':
      return {bg: p.card, border: p.border, ink: p.foreground};
  }
}

/** «09–18», а с минутами — «09:30–18». */
function hours(start: number | null, end: number | null): string | null {
  if (start == null || end == null) {
    return null;
  }
  const short = (m: number) => {
    const s = formatDayMinutes(m) ?? '';
    return s.endsWith(':00') ? s.slice(0, 2) : s;
  };
  return `${short(start)}–${short(end)}`;
}

export function MonthGrid({
  cells,
  today,
  selected,
  onPress,
  onPaint,
  onWeekdayPress,
}: {
  cells: ReadonlyArray<MonthCell>;
  today: string;
  /** Выделенные дни. */
  selected?: ReadonlySet<string>;
  /** `shift` — нажатие с Shift (веб): выделить диапазон. */
  onPress?: (cell: MonthCell, shift: boolean) => void;
  /** Протягивание отпущено: задетые дни и что с ними сделать. */
  onPaint?: (dates: string[], mode: PaintMode) => void;
  /** Нажатие на заголовок столбца: 0 — пн … 6 — вс. */
  onWeekdayPress?: (weekday: number) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {isTablet} = useBreakpoint();
  const height = isTablet ? 76 : 58;

  // ── Протягивание ───────────────────────────────────────────────────────────
  // Прямоугольники клеток в координатах сетки: строка знает свой y, клетка — свой x.
  const rowY = useRef(new Map<number, number>());
  const cellX = useRef(new Map<string, {x: number; w: number; row: number}>());
  const rootRef = useRef<View>(null);
  const rects = (): Map<string, Rect> => {
    const out = new Map<string, Rect>();
    for (const [date, c] of cellX.current) {
      const y = rowY.current.get(c.row);
      if (y != null) {
        out.set(date, {x: c.x, y, w: c.w, h: height});
      }
    }
    return out;
  };

  // Предпросмотр: что задето и что с ним будет. Решение — на отпускании.
  const [preview, setPreview] = useState<{dates: Set<string>; mode: PaintMode} | null>(null);
  const drag = useRef<{
    origin: Point | null;
    start: Point;
    last: Point;
    first: string | null;
    mode: PaintMode;
    dates: Set<string>;
    moved: boolean;
    shift: boolean;
  } | null>(null);

  // Обработчики живут в PanResponder, созданном один раз: свежие пропсы — через ref.
  const latest = useRef({selected, onPress, onPaint, cells});
  latest.current = {selected, onPress, onPaint, cells};
  const enabled = !!onPress || !!onPaint;

  const local = (e: GestureResponderEvent, origin: Point): Point => ({
    x: e.nativeEvent.pageX - origin.x,
    y: e.nativeEvent.pageY - origin.y,
  });

  const responder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponderCapture: () => enabled,
      onMoveShouldSetPanResponderCapture: () => enabled,
      // Не отдавать жест прокрутке посреди протягивания.
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: e => {
        const page = {x: e.nativeEvent.pageX, y: e.nativeEvent.pageY};
        const shift = !!(e.nativeEvent as unknown as {shiftKey?: boolean}).shiftKey;
        drag.current = {
          origin: null,
          start: page,
          last: page,
          first: null,
          mode: 'add',
          dates: new Set(),
          moved: false,
          shift,
        };
        rootRef.current?.measure((_x, _y, _w, _h, px, py) => {
          const d = drag.current;
          if (!d) {
            return;
          }
          d.origin = {x: px, y: py};
          const at = {x: d.start.x - px, y: d.start.y - py};
          d.first = hitTest(rects(), at);
          d.last = at;
          if (d.first) {
            d.mode = latest.current.selected?.has(d.first) ? 'remove' : 'add';
            d.dates.add(d.first);
          }
        });
      },
      onPanResponderMove: e => {
        const d = drag.current;
        if (!d || !d.origin) {
          return;
        }
        const pt = local(e, d.origin);
        const page = {x: e.nativeEvent.pageX, y: e.nativeEvent.pageY};
        if (!d.moved && Math.hypot(page.x - d.start.x, page.y - d.start.y) < TAP_SLOP) {
          return;
        }
        d.moved = true;
        for (const date of datesAlong(rects(), d.last, pt)) {
          d.dates.add(date);
        }
        d.last = pt;
        setPreview({dates: new Set(d.dates), mode: d.mode});
      },
      onPanResponderRelease: () => {
        const d = drag.current;
        drag.current = null;
        setPreview(null);
        if (!d) {
          return;
        }
        if (!d.moved) {
          // Нажатие, а не протягивание: то же, что клик по клетке.
          const cell = latest.current.cells.find(c => c.date === d.first);
          if (cell) {
            latest.current.onPress?.(cell, d.shift);
          }
          return;
        }
        if (d.dates.size > 0) {
          latest.current.onPaint?.([...d.dates], d.mode);
        }
      },
      onPanResponderTerminate: () => {
        drag.current = null;
        setPreview(null);
      },
    }),
  ).current;

  const shown = preview ? paint(selected ?? new Set(), preview.dates, preview.mode) : selected;

  const weeks: MonthCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const label = (c: MonthCell): string | null => {
    switch (c.kind) {
      case 'holiday':
        return isTablet && c.exception?.title ? c.exception.title : t('schedule.cal.short.holiday');
      case 'day_off':
        return t('schedule.cal.short.day_off');
      case 'workday':
        return hours(c.start, c.end) ?? t('schedule.cal.short.workday');
      case 'off':
        return t('schedule.cal.short.off');
      case 'work':
        return hours(c.start, c.end);
      case 'plain':
        return null;
    }
  };

  return (
    <View style={styles.root} testID="month-grid">
      <View style={styles.row}>
        {[0, 1, 2, 3, 4, 5, 6].map(d => (
          <Pressable
            key={d}
            onPress={onWeekdayPress ? () => onWeekdayPress(d) : undefined}
            accessibilityRole={onWeekdayPress ? 'button' : undefined}
            accessibilityLabel={
              onWeekdayPress
                ? t('schedule.cal.selectWeekday', {day: t(`home.person.weekday.${d}`)})
                : undefined
            }
            testID={`weekday-${d}`}
            style={({pressed}) => [styles.head, pressed && {backgroundColor: p.muted}]}>
            <AppText variant="caption">{t(`home.person.weekday.${d}`)}</AppText>
          </Pressable>
        ))}
      </View>
      <View
        ref={rootRef}
        style={styles.body}
        {...(enabled ? responder.panHandlers : {})}
        testID="month-days">
        {weeks.map((week, row) => (
          <View
            key={week[0].date}
            style={styles.row}
            onLayout={(e: LayoutChangeEvent) => rowY.current.set(row, e.nativeEvent.layout.y)}>
            {week.map(c => {
              const skin = skinOf(c.kind, p);
              const isToday = c.date === today;
              const isSelected = !!shown?.has(c.date);
              const text = label(c);
              return (
                <Pressable
                  key={c.date}
                  onLayout={(e: LayoutChangeEvent) =>
                    cellX.current.set(c.date, {
                      x: e.nativeEvent.layout.x,
                      w: e.nativeEvent.layout.width,
                      row,
                    })
                  }
                  // Мышь и палец ведёт сетка (протягивание); onPress остаётся для
                  // клавиатуры и экранного диктора.
                  onPress={
                    onPress
                      ? e =>
                          onPress(c, !!(e.nativeEvent as unknown as {shiftKey?: boolean}).shiftKey)
                      : undefined
                  }
                  accessibilityRole={onPress ? 'checkbox' : undefined}
                  accessibilityState={onPress ? {checked: isSelected} : undefined}
                  accessibilityLabel={[c.date, text].filter(Boolean).join(', ')}
                  testID={`day-${c.date}`}
                  style={({pressed}) => [
                    styles.cell,
                    {
                      height,
                      backgroundColor: isSelected
                        ? withAlpha(p.primary, 0.22)
                        : pressed
                        ? p.muted
                        : skin.bg,
                      borderColor: isSelected || isToday ? p.primary : skin.border,
                      borderWidth: isSelected ? 2 : 1,
                      opacity: c.inMonth ? 1 : 0.4,
                    },
                  ]}>
                  <View style={styles.top}>
                    <AppText
                      variant="label"
                      color={
                        isToday ? p.primary : c.kind === 'off' ? p.mutedForeground : p.foreground
                      }>
                      {String(c.day)}
                    </AppText>
                    {isSelected ? (
                      <Icon name="check" size={12} color={p.primary} />
                    ) : c.inherited ? (
                      <View style={[styles.dot, {backgroundColor: skin.ink}]} />
                    ) : null}
                  </View>
                  {text ? (
                    <AppText variant="micro" color={skin.ink} numberOfLines={isTablet ? 2 : 1}>
                      {text}
                    </AppText>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}
